import React, { useCallback, useRef, useState, useContext, useMemo } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import CloseButton from 'components/Common/CloseButton'
import InlineGroup from 'components/Common/InlineGroup'
import TextButton from 'components/Luxkit/Button/TextButton'
import CalendarMonthPicker from './CalendarMonthPicker'
import CalendarDayPicker from './CalendarDayPicker'
import CalendarDurationSelect from './CalendarDurationSelect'
import CalendarSidebarDayPicker from './CalendarSidebarDayPicker'
import IconButton from 'components/Luxkit/Button/IconButton'
import Caption from 'components/Luxkit/Typography/Caption'
import LineAngleLeftBIcon from 'components/Luxkit/Icons/line/LineAngleLeftBIcon'
import eases from 'styles/tools/eases'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { pluralizeToString } from 'lib/pluralize'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { groupBy } from 'lib/arrayUtils'
import { getSelectedDay, getCalendarDates, isBedbankCalendarFetching } from 'selectors/bedbankOfferSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isBedbankFlightBookingEnabled, isFlightBedbankCalendarFetching } from 'selectors/flightsSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import CalendarPickerRecommendations from 'components/OfferPage/Filters/CalendarPicker/Recommendations/CalendarPickerRecommendations'
import CalendarPickerRecommendationsHeading from 'components/OfferPage/Filters/CalendarPicker/Recommendations/CalendarPickerRecommendationsHeading'
import OfferCalendarFlightsToggle from 'components/OfferPage/Filters/CalendarPicker/OfferCalendarFlightsToggle'
import CalendarRoomSelect from './CalendarRoomSelect'

const FadingBackButton = styled(IconButton)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const Calendar = styled.div`
  padding: ${rem(20)} 0;
  flex-grow: 1;
  min-height: ${rem(400)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }
`

const Header = styled(InlineGroup)`
  padding: ${rem(16)} ${rem(40)};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  background-color: ${props => props.theme.palette.neutral.eight};
  z-index: 2;
  border-top-left-radius: ${props => props.theme.borderRadius.large};
  border-top-right-radius: ${props => props.theme.borderRadius.large};

  > * + * {
    margin-left: ${rem(32)}
  }
`

const Content = styled.div`
  position: relative;
  overflow: hidden;
  border-bottom-left-radius: ${props => props.theme.borderRadius.large};
  border-bottom-right-radius: ${props => props.theme.borderRadius.large};
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${rem(20)} ${rem(40)};
  display: flex;
  justify-content: space-between;
  box-shadow: ${props => props.theme.shadow.flat.small};
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.eight};
  z-index: 1;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: sticky;
    bottom: 0;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const FooterActions = styled(InlineGroup)`
  text-align: right;
`

const Scroller = styled.div`
  overflow: auto;
`

const HeaderButtons = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  > * + * {
    margin-left: ${rem(12)};
  }
`

const RecommendationHeadingWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const FetchingCalendarsIndicator = styled(LoadingIndicator)`
  padding: 0;
`

const Filters = styled.div`
  display: flex;
  position: absolute;
  margin: ${rem(-12)} ${rem(-24)} 0;
  top: 0;
  right: 0;
  left: 0;
  padding: ${rem(12)};
  position: sticky;
  z-index: 1;
  background-color: ${props => props.theme.palette.neutral.eight};
  width: calc(100% + ${rem(48)});
  justify-content: center;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  gap: ${rem(8)};
`

interface Props {
  duration: number;
  fetching: boolean;
  flightFetching: boolean;
  flights?: boolean;
  airport?: App.Airport;
  offer: App.BedbankOffer;
  selectedDay?: App.BedbankCalendarDay;
  calendarDates: { [month: string]: App.BedbankCalendarMonth };
  isBedbankFlightBookingEnabled: boolean;
  single?: boolean;
  rooms?: App.Occupants[];
  recommendationsShown: boolean;
}

function CalendarDatePicker({
  fetching,
  flightFetching,
  duration,
  single,
  selectedDay,
  calendarDates,
  rooms,
  flights,
  offer,
  isBedbankFlightBookingEnabled,
  recommendationsShown,
}: Props) {
  const containerRef = useRef<HTMLDivElement>()
  const [monthIndex, setMonth] = useState<string | undefined>()

  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, recommendationsShown])

  const onDatesChange = useCallback((day: App.BedbankCalendarDay) => {
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: moment(day.checkIn) })
    dispatch({ type: OfferPageActions.setCheckOutDate, checkOut: moment(day.checkOut) })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [dispatch, onUserAnalyticsAction])

  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  const onApply = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const flightsEnabled = offer?.flights?.flightsEnabled && isBedbankFlightBookingEnabled && occupancyNumber < 10

  const daysByMonth = useMemo(() => {
    return Object.values(calendarDates)
  }, [calendarDates])

  const monthsByYear = useMemo(() => {
    return groupBy(daysByMonth, (x) => x.year)
  }, [daysByMonth])

  const fetchingData = (flights && flightFetching) || (!flights && fetching)

  return (<>
    {!single && !recommendationsShown && <Header horizontal="between" vertical="center">
      <FadingBackButton
        kind="tertiary"
        className={cn({ visible: monthIndex !== undefined })}
        outdent="left"
        size="large"
        onClick={onBackToMonth}
      >
        <LineAngleLeftBIcon />
      </FadingBackButton>
      <HeaderButtons>
        {fetchingData && <BodyText variant="medium" weight="bold">Finding availability</BodyText>}
        <FetchingCalendarsIndicator inline visible={fetchingData} />
        <CalendarRoomSelect />
        <CalendarDurationSelect />
        {flightsEnabled && <OfferCalendarFlightsToggle />}
      </HeaderButtons>
      <CloseButton outdent="right" size="large" onClick={onDropdownClose} />
    </Header>}
    {
      !single && recommendationsShown && offer &&
        <Header vertical="center">
          <FadingBackButton
            kind="tertiary"
            className={cn({ visible: monthIndex !== undefined })}
            outdent="left"
            size="large"
            onClick={onBackToMonth}
          >
            <LineAngleLeftBIcon />
          </FadingBackButton>
          <RecommendationHeadingWrapper>
            <ErrorBoundary fallback={null}>
              <CalendarPickerRecommendationsHeading />
            </ErrorBoundary>
          </RecommendationHeadingWrapper>
          <CloseButton outdent="right" size="large" onClick={onDropdownClose} />
        </Header>
    }
    {single && !recommendationsShown && <Filters>
      <CalendarRoomSelect />
      <CalendarDurationSelect sidebar />
      {flightsEnabled && <OfferCalendarFlightsToggle />}
    </Filters>}
    <Content>
      <Scroller ref={containerRef}>
        <Calendar>
          {recommendationsShown && offer && (
            <ErrorBoundary fallback={null}>
              <CalendarPickerRecommendations
                offerId={offer.id}
                offerType={offer.type}
                rooms={rooms}
                heading={`Similar hotels to ${offer.name ?? offer.property?.location}`}
                seedOffer={offer}
              />
            </ErrorBoundary>
          )}
          {
            !recommendationsShown && (<>
              {monthIndex === undefined && <CalendarMonthPicker onMonthSelect={onMonthSelect} monthsByYear={monthsByYear} duration={duration} selectedDay={selectedDay} fetching={fetchingData} flights={flights} />}
              {monthIndex !== undefined && !single && !fetchingData && <CalendarDayPicker monthIndex={monthIndex} daysByMonth={daysByMonth} contentRef={containerRef} duration={duration} onDatesChange={onDatesChange} fetching={fetchingData} flights={flights}/>}
              {monthIndex !== undefined && single && !fetchingData && <CalendarSidebarDayPicker monthIndex={monthIndex} daysByMonth={daysByMonth} duration={duration} onDatesChange={onDatesChange} onBackToMonth={onBackToMonth} fetching={fetchingData} flights={flights}/>}
            </>)
          }
        </Calendar>
      </Scroller>
      <Footer className={cn({ visible: !!selectedDay && !fetchingData })}>
        <TextButton kind="secondary" size="large" onClick={onResetDates}>
          Reset dates
        </TextButton>
        <FooterActions vertical="center" spacing="xxl">
          {selectedDay?.price && selectedDay?.hasPromo && <div>
            <Caption variant="large">
              {pluralizeToString('night', duration)} {flights && '+ flights '} {' from'}
            </Caption>
            <Heading as="span" variant="heading5">
              <FormatCurrency value={selectedDay.price + (flights && selectedDay.flightPrice ? selectedDay.flightPrice : 0)} />
            </Heading>{' '}
            <Caption as="span" variant="large">total</Caption>
          </div>}
          <TextButton kind="primary" size="large" onClick={onApply}>
            Apply
          </TextButton>
        </FooterActions>
      </Footer>
    </Content>
  </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  selectedDay: getSelectedDay(state, appState),
  calendarDates: getCalendarDates(state, appState),
  duration: state.duration,
  flights: state.flights,
  rooms: state.rooms,
  offer: state.bedbankOffer,
  fetching: isBedbankCalendarFetching(state, appState),
  flightFetching: isFlightBedbankCalendarFetching(state, appState),
  isBedbankFlightBookingEnabled: isBedbankFlightBookingEnabled(appState),
  recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(appState, state.bedbankOffer),
}))(CalendarDatePicker)
